import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Users, Vote, Trophy, TrendingUp, MapPin, GitCompare, X, ChevronDown, ChevronUp } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Party-specific colors
const PARTY_COLORS = {
  'BJP': '#FF9933',
  'INC': '#19AAED',
  'NCP': '#00BFFF',
  'SS': '#FF6600',
  'SS-UBT': '#F97316',
  'BSP': '#22C55E',
  'AIMIM': '#22C55E',
  'IUML': '#138808',
  'MNS': '#FF6600',
  'NCP-SP': '#00CED1',
};

const ZONE_COLORS = ['#003366', '#FF9933', '#138808'];

const DEFAULT_COLORS = ['#6366F1', '#EC4899', '#8B5CF6', '#14B8A6', '#F59E0B', '#EF4444', '#3B82F6'];

const getPartyColor = (partyName, index) => {
  return PARTY_COLORS[partyName] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
};

const ZoneSummary = () => {
  const { t, language } = useLanguage();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedZone, setSelectedZone] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/zone-summary`);
      setData(response.data);
      if (response.data.length > 0) {
        setSelectedZone(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching zone summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCompareMode = () => {
    setCompareMode(!compareMode);
    setSelectedForCompare([]);
  };

  const toggleZoneForCompare = (zone) => {
    if (selectedForCompare.find(z => z.zone === zone.zone)) {
      setSelectedForCompare(selectedForCompare.filter(z => z.zone !== zone.zone));
    } else if (selectedForCompare.length < 3) {
      setSelectedForCompare([...selectedForCompare, zone]);
    }
  };

  const isZoneSelected = (zoneName) => {
    return selectedForCompare.some(z => z.zone === zoneName);
  };

  if (loading) {
    return <div className={`text-center py-12 ${language === 'mr' ? 'marathi-text' : ''}`}>{t('loading')}</div>;
  }

  const zoneLabels = {
    'Zone 1': language === 'en' ? 'Laxmi Nagar Zone' : 'लक्ष्मीनगर झोन',
    'Zone 2': language === 'en' ? 'Dharampeth Zone' : 'धरमपेठ झोन',
    'Zone 3': language === 'en' ? 'Hanuman Nagar Zone' : 'हनुमान नगर झोन',
    'Zone 4': language === 'en' ? 'Dhantoli Zone' : 'धंतोली झोन',
    'Zone 5': language === 'en' ? 'Nehru Nagar Zone' : 'नेहरू नगर झोन',
    'Zone 6': language === 'en' ? 'Gandhibagh Zone' : 'गांधीबाग झोन',
    'Zone 7': language === 'en' ? 'Sataranjipura Zone' : 'सातारंजीपुरा झोन',
    'Zone 8': language === 'en' ? 'Lakadganj Zone' : 'लकडगंज झोन',
    'Zone 9': language === 'en' ? 'Ashi Nagar Zone' : 'आशी नगर झोन',
    'Zone 10': language === 'en' ? 'Mangalwari Zone' : 'मंगळवारी झोन',
  };

  // Prepare chart data for selected zone
  const chartData = selectedZone ? 
    Object.entries(selectedZone.party_distribution)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value) 
    : [];

  // Prepare comparison bar chart data
  const comparisonData = data.map(zone => ({
    name: zone.zone.replace('Zone ', 'Z'),
    seats: zone.total_seats,
    polling: zone.avg_polling_percentage,
  }));

  // Get all unique parties across selected zones for comparison
  const getAllPartiesFromZones = (zones) => {
    const parties = new Set();
    zones.forEach(zone => {
      Object.keys(zone.party_distribution).forEach(party => parties.add(party));
    });
    return Array.from(parties);
  };

  // Prepare radar chart data for comparison
  const prepareRadarData = () => {
    if (selectedForCompare.length < 2) return [];
    
    const allParties = getAllPartiesFromZones(selectedForCompare);
    return allParties.map(party => {
      const dataPoint = { party };
      selectedForCompare.forEach((zone, index) => {
        dataPoint[zone.zone] = zone.party_distribution[party] || 0;
      });
      return dataPoint;
    });
  };

  // Prepare comparison stats
  const prepareComparisonStats = () => {
    return selectedForCompare.map(zone => ({
      zone: zone.zone,
      label: zoneLabels[zone.zone] || zone.zone,
      seats: zone.total_seats,
      voters: zone.total_voters,
      polling: zone.avg_polling_percentage,
      topParty: Object.keys(zone.party_distribution).sort((a, b) => 
        zone.party_distribution[b] - zone.party_distribution[a]
      )[0],
      topPartySeats: Math.max(...Object.values(zone.party_distribution)),
      partyCount: Object.keys(zone.party_distribution).length,
    }));
  };

  return (
    <div className="space-y-6" data-testid="zone-summary-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className={`text-3xl font-bold text-primary mb-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
            {language === 'en' ? 'Zone-wise Results' : 'झोननुसार निकाल'}
          </h1>
          <p className={`text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
            {language === 'en' ? 'Summary of election results by zone' : 'झोननुसार निवडणूक निकालांचा सारांश'}
          </p>
        </div>
        <Button
          onClick={toggleCompareMode}
          variant={compareMode ? "default" : "outline"}
          className="flex items-center gap-2"
          data-testid="compare-mode-btn"
        >
          <GitCompare className="h-4 w-4" />
          {compareMode 
            ? (language === 'en' ? 'Exit Compare' : 'तुलना बंद करा')
            : (language === 'en' ? 'Compare Zones' : 'झोन तुलना')
          }
        </Button>
      </div>

      {/* Compare Mode Instructions */}
      {compareMode && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <GitCompare className="h-5 w-5 text-primary" />
                <span className={language === 'mr' ? 'marathi-text' : ''}>
                  {language === 'en' 
                    ? `Select 2-3 zones to compare (${selectedForCompare.length}/3 selected)`
                    : `तुलना करण्यासाठी 2-3 झोन निवडा (${selectedForCompare.length}/3 निवडले)`
                  }
                </span>
              </div>
              {selectedForCompare.length >= 2 && (
                <div className="flex gap-2">
                  {selectedForCompare.map((zone, idx) => (
                    <Badge 
                      key={zone.zone}
                      style={{ backgroundColor: ZONE_COLORS[idx] }}
                      className="text-white cursor-pointer"
                      onClick={() => toggleZoneForCompare(zone)}
                    >
                      {zone.zone} <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Zone Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {data.map((zone) => (
          <Card 
            key={zone.zone}
            className={`cursor-pointer transition-all hover:shadow-lg ${
              compareMode 
                ? isZoneSelected(zone.zone) 
                  ? 'ring-2 ring-primary bg-primary/5' 
                  : ''
                : selectedZone?.zone === zone.zone 
                  ? 'ring-2 ring-primary' 
                  : ''
            }`}
            onClick={() => compareMode ? toggleZoneForCompare(zone) : setSelectedZone(zone)}
            data-testid={`zone-card-${zone.zone.replace(' ', '-').toLowerCase()}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  {zone.zone}
                </CardTitle>
                {compareMode && (
                  <Checkbox 
                    checked={isZoneSelected(zone.zone)}
                    disabled={!isZoneSelected(zone.zone) && selectedForCompare.length >= 3}
                  />
                )}
              </div>
              <p className={`text-xs text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
                {zoneLabels[zone.zone] || zone.zone}
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">{language === 'en' ? 'Seats' : 'जागा'}</span>
                  <span className="font-bold">{zone.total_seats}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">{language === 'en' ? 'Polling' : 'मतदान'}</span>
                  <span className="font-bold">{zone.avg_polling_percentage}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">{language === 'en' ? 'Top Party' : 'शीर्ष पक्ष'}</span>
                  <Badge 
                    style={{ backgroundColor: getPartyColor(Object.keys(zone.party_distribution).sort((a, b) => 
                      zone.party_distribution[b] - zone.party_distribution[a]
                    )[0], 0) }}
                    className="text-white text-xs"
                  >
                    {Object.keys(zone.party_distribution).sort((a, b) => 
                      zone.party_distribution[b] - zone.party_distribution[a]
                    )[0] || '-'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Comparison View */}
      {compareMode && selectedForCompare.length >= 2 && (
        <div className="space-y-6">
          {/* Comparison Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {prepareComparisonStats().map((stat, idx) => (
              <Card key={stat.zone} style={{ borderTop: `4px solid ${ZONE_COLORS[idx]}` }}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: ZONE_COLORS[idx] }}
                    />
                    {stat.zone}
                  </CardTitle>
                  <p className={`text-sm text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
                    {stat.label}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">{language === 'en' ? 'Total Seats' : 'एकूण जागा'}</p>
                      <p className="text-2xl font-bold">{stat.seats}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{language === 'en' ? 'Polling %' : 'मतदान %'}</p>
                      <p className="text-2xl font-bold">{stat.polling}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{language === 'en' ? 'Total Voters' : 'एकूण मतदार'}</p>
                      <p className="text-lg font-semibold">{stat.voters.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{language === 'en' ? 'Leading Party' : 'अग्रणी पक्ष'}</p>
                      <Badge style={{ backgroundColor: getPartyColor(stat.topParty, 0) }} className="text-white">
                        {stat.topParty} ({stat.topPartySeats})
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Party Performance Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bar Chart Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
                  <TrendingUp className="h-5 w-5 text-primary" />
                  {language === 'en' ? 'Party Seats Comparison' : 'पक्ष जागा तुलना'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={(() => {
                    const allParties = getAllPartiesFromZones(selectedForCompare);
                    return allParties.map(party => {
                      const item = { party };
                      selectedForCompare.forEach(zone => {
                        item[zone.zone] = zone.party_distribution[party] || 0;
                      });
                      return item;
                    }).sort((a, b) => {
                      const totalA = selectedForCompare.reduce((sum, z) => sum + (a[z.zone] || 0), 0);
                      const totalB = selectedForCompare.reduce((sum, z) => sum + (b[z.zone] || 0), 0);
                      return totalB - totalA;
                    });
                  })()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="party" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {selectedForCompare.map((zone, idx) => (
                      <Bar 
                        key={zone.zone} 
                        dataKey={zone.zone} 
                        fill={ZONE_COLORS[idx]} 
                        name={zone.zone}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Radar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
                  <Vote className="h-5 w-5 text-primary" />
                  {language === 'en' ? 'Party Distribution Pattern' : 'पक्ष वितरण पॅटर्न'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={prepareRadarData()}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="party" />
                    <PolarRadiusAxis />
                    {selectedForCompare.map((zone, idx) => (
                      <Radar 
                        key={zone.zone}
                        name={zone.zone} 
                        dataKey={zone.zone} 
                        stroke={ZONE_COLORS[idx]} 
                        fill={ZONE_COLORS[idx]} 
                        fillOpacity={0.3}
                      />
                    ))}
                    <Legend />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Side by Side Winners */}
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
                <Trophy className="h-5 w-5 text-accent" />
                {language === 'en' ? 'Top Winners Comparison' : 'शीर्ष विजेते तुलना'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`grid grid-cols-1 md:grid-cols-${selectedForCompare.length} gap-4`}>
                {selectedForCompare.map((zone, idx) => (
                  <div key={zone.zone}>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: ZONE_COLORS[idx] }}
                      />
                      {zone.zone} - {language === 'en' ? 'Top 3' : 'शीर्ष 3'}
                    </h3>
                    <div className="space-y-2">
                      {zone.top_winners.slice(0, 3).map((winner, wIdx) => (
                        <div 
                          key={wIdx} 
                          className="p-3 bg-muted/50 rounded-lg"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-sm">{winner.winner_name}</p>
                              <p className="text-xs text-muted-foreground">
                                Ward {winner.ward_no}-{winner.seat_no} • {winner.reservation}
                              </p>
                            </div>
                            <Badge 
                              style={{ backgroundColor: getPartyColor(winner.winner_party, 0) }}
                              className="text-white text-xs"
                            >
                              {winner.winner_party}
                            </Badge>
                          </div>
                          <div className="flex justify-between mt-2 text-xs">
                            <span>{language === 'en' ? 'Votes' : 'मते'}: {winner.winner_votes.toLocaleString()}</span>
                            <span className="font-bold text-accent">
                              {language === 'en' ? 'Margin' : 'मताधिक्य'}: {winner.margin.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Detailed Comparison Table */}
          <Card>
            <CardHeader>
              <CardTitle className={language === 'mr' ? 'marathi-text' : ''}>
                {language === 'en' ? 'Detailed Comparison' : 'तपशीलवार तुलना'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{language === 'en' ? 'Metric' : 'मापदंड'}</TableHead>
                    {selectedForCompare.map((zone, idx) => (
                      <TableHead key={zone.zone}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: ZONE_COLORS[idx] }}
                          />
                          {zone.zone}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">{language === 'en' ? 'Total Seats' : 'एकूण जागा'}</TableCell>
                    {selectedForCompare.map(zone => (
                      <TableCell key={zone.zone}>{zone.total_seats}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">{language === 'en' ? 'Total Voters' : 'एकूण मतदार'}</TableCell>
                    {selectedForCompare.map(zone => (
                      <TableCell key={zone.zone}>{zone.total_voters.toLocaleString()}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">{language === 'en' ? 'Polling %' : 'मतदान %'}</TableCell>
                    {selectedForCompare.map(zone => (
                      <TableCell key={zone.zone}>{zone.avg_polling_percentage}%</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">{language === 'en' ? 'Parties Won' : 'पक्ष विजयी'}</TableCell>
                    {selectedForCompare.map(zone => (
                      <TableCell key={zone.zone}>{Object.keys(zone.party_distribution).length}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">BJP</TableCell>
                    {selectedForCompare.map(zone => (
                      <TableCell key={zone.zone}>
                        <Badge style={{ backgroundColor: '#FF9933' }} className="text-white">
                          {zone.party_distribution['BJP'] || 0}
                        </Badge>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">INC</TableCell>
                    {selectedForCompare.map(zone => (
                      <TableCell key={zone.zone}>
                        <Badge style={{ backgroundColor: '#19AAED' }} className="text-white">
                          {zone.party_distribution['INC'] || 0}
                        </Badge>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">{language === 'en' ? 'Others' : 'इतर'}</TableCell>
                    {selectedForCompare.map(zone => {
                      const others = Object.entries(zone.party_distribution)
                        .filter(([party]) => party !== 'BJP' && party !== 'INC')
                        .reduce((sum, [, count]) => sum + count, 0);
                      return <TableCell key={zone.zone}>{others}</TableCell>;
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Single Zone View (when not in compare mode or less than 2 zones selected) */}
      {(!compareMode || selectedForCompare.length < 2) && selectedZone && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Party Distribution Chart */}
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
                <Vote className="h-5 w-5 text-primary" />
                {selectedZone.zone} - {language === 'en' ? 'Party Distribution' : 'पक्ष वितरण'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${entry.value}`}
                      outerRadius={100}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getPartyColor(entry.name, index)} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-muted-foreground">{t('noData')}</div>
              )}
            </CardContent>
          </Card>

          {/* Top Winners Table */}
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
                <Trophy className="h-5 w-5 text-accent" />
                {selectedZone.zone} - {language === 'en' ? 'Top Winners (Highest Margin)' : 'शीर्ष विजेते (सर्वाधिक मताधिक्य)'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{language === 'en' ? 'Ward' : 'प्रभाग'}</TableHead>
                    <TableHead>{language === 'en' ? 'Winner' : 'विजेता'}</TableHead>
                    <TableHead>{language === 'en' ? 'Party' : 'पक्ष'}</TableHead>
                    <TableHead>{language === 'en' ? 'Votes' : 'मते'}</TableHead>
                    <TableHead>{language === 'en' ? 'Margin' : 'मताधिक्य'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedZone.top_winners.map((winner, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{winner.ward_no}-{winner.seat_no}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{winner.winner_name}</div>
                          <div className="text-xs text-muted-foreground">{winner.reservation}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge style={{ backgroundColor: getPartyColor(winner.winner_party, 0) }} className="text-white">
                          {winner.winner_party}
                        </Badge>
                      </TableCell>
                      <TableCell>{winner.winner_votes.toLocaleString()}</TableCell>
                      <TableCell className="font-bold text-accent">{winner.margin.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Zone Comparison Chart (always visible) */}
      {!compareMode && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
                <TrendingUp className="h-5 w-5 text-primary" />
                {language === 'en' ? 'Zone Comparison' : 'झोन तुलना'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" orientation="left" stroke="#003366" />
                  <YAxis yAxisId="right" orientation="right" stroke="#FF9933" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="seats" fill="#003366" name={language === 'en' ? 'Seats' : 'जागा'} />
                  <Bar yAxisId="right" dataKey="polling" fill="#FF9933" name={language === 'en' ? 'Polling %' : 'मतदान %'} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          {selectedZone && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">
                    {language === 'en' ? 'Total Seats' : 'एकूण जागा'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{selectedZone.total_seats}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">
                    {language === 'en' ? 'Total Voters' : 'एकूण मतदार'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{selectedZone.total_voters.toLocaleString()}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">
                    {language === 'en' ? 'Avg Polling' : 'सरासरी मतदान'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{selectedZone.avg_polling_percentage}%</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">
                    {language === 'en' ? 'Leading Party' : 'अग्रणी पक्ष'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge 
                    style={{ backgroundColor: getPartyColor(Object.keys(selectedZone.party_distribution).sort((a, b) => selectedZone.party_distribution[b] - selectedZone.party_distribution[a])[0], 0) }}
                    className="text-white text-lg px-3 py-1"
                  >
                    {Object.keys(selectedZone.party_distribution).sort((a, b) => selectedZone.party_distribution[b] - selectedZone.party_distribution[a])[0]}
                  </Badge>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ZoneSummary;
